import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ConfigProvider } from 'tea-component';
import { useIntl } from 'umi';
import RangePicker from '@/pages/home/components/RangePicker';
import moment, { Moment } from 'moment';
import SingleSelect from '@home/components/SingleSelect';
import { getLanguage, getName } from '@home/utils/common';
import Abnormal from '@home/components/Abnormal';
import { LANGUAGE_CN, LANGUAGE_EN } from '@home/_config/config';
import { dateFilterConfig } from './_config';
import { formatGameList, formatLanguageList, formatListData } from './_utils';
import { StudioContext } from '@home/pages/studio/context';
import { getSocialSeletorOptions } from '@home/services/studio';
import { ISocialFilter } from './_type';
import FilterSelect from '@/pages/dashboard/components/FilterSelect';
import AIX from '@/pages/home/assets/svg/studio/socialIndex/aix-icon.svg';

import './index.scss';

export default function SocialFilter(props: ISocialFilter) {
  const { children, granularity = 'week', gameFitlerConfig, languageFilterConfig, rightTextEn, rightTextCn } = props;
  const { currentGame } = useContext(StudioContext);
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const [timeValue, setTimeValue] = useState<[Moment, Moment]>(dateFilterConfig.defaultTimeValue);
  // 游戏筛选器选中的值
  const [game, setGame] = useState<string>();
  // 游戏筛选器下拉数据
  const [gameList, setGameList] = useState([]);
  // 语言筛选器下拉数据
  const [languageList, setLanguageList] = useState([]);
  // 语言筛选器选中的值
  const [language, setLanguage] = useState<string[]>([]);
  // 传给子组件的筛选器数据
  const [metricsList, setMetricsList] = useState<string[]>([]);
  const [selfGame, setSelfGame] = useState({});
  const [filterLoading, setFilterLoading] = useState(true);
  const [error, setError] = useState(false);
  const bindClassName = useRef(Date.now().toString(36));

  const {
    enName: gameFilterEnName = 'Competing Games',
    cnName: gameFilterCnName = '竞品游戏',
    enTooltip: gameFilterEnTooltip = '',
    cnTooltip: gameFilterCnTooltip = '',
    defualt: gameDefault = 'e11000000288',
  } = gameFitlerConfig || {};

  const {
    enName: languageFilterEnName = 'Language ',
    cnName: languageFilterCnName = '语言',
    enTooltip: languageFilterEnTooltip = '',
    cnTooltip: languageFilterCnTooltip = '',
    defualt: langDefault = ['global', 'en', 'de', 'jp', 'fr', 'kr'],
    // 最多9个， 但是加上glbal就是10个
    maxSelectedNum = 10,
  } = languageFilterConfig || {};

  const gameInfo = useMemo(() => {
    const {
      unified_edition_id: edition_unified_id,
      id_type,
      game_type: entity_type,
      studio_code
    } = currentGame || {};

    return {
      edition_unified_id,
      id_type,
      entity_type,
      studio_code
    };
  }, [currentGame]);

  // 传给子组件的筛选器的值
  const filter = useMemo(() => {
    const timeVal = dateFilterConfig.formatTime(timeValue);
    return {
      start_time: timeVal[0],
      end_time: timeVal[1],
      date_type: dateFilterConfig.type,
      isSingle: dateFilterConfig.isSingle(timeValue[0], timeValue[1]),
      game,
      language
    };
  }, [timeValue, game, language]);

  useEffect(() => {
    setLanguage(langDefault);
    setGame(gameDefault);
  }, [langDefault?.toString(), gameDefault]);

  // 获取筛选器下拉数据
  useEffect(() => {
    setFilterLoading(true);
    const params = {
      unified_id: gameInfo?.edition_unified_id,
    };
    getSocialSeletorOptions(params).then((res) => {
      setGameList(res?.game_list || []);
      setLanguageList(res?.language_list || []);
      setMetricsList(res?.metric_list || []);
      setSelfGame(res?.self_game || {});
    }).catch(() => {
      setError(true);
    }).finally(() => {
      setFilterLoading(false);
    });
  }, [gameInfo]);

  // 格式化组件需要的语言和游戏下拉数据
  const { languageOptions, gameOptions } = useMemo(() => ({
    languageOptions: formatLanguageList(languageList, isEn),
    gameOptions: formatGameList(gameList, isEn)
  }), [languageList, gameList, isEn]);

  const { gameMap, languageMap, metricsKeyList } = useMemo(() => ({
    gameMap: {
      [selfGame?.unified_edition_id]: {
        unified_edition_id: selfGame?.unified_edition_id,
        name: getLanguage(isEn, selfGame?.name_en, selfGame?.name_cn),
        cover: selfGame?.cover || '',
      },
      ...(formatListData(gameList, 'unified_edition_id', isEn) || {})
    },
    languageMap: formatListData(languageList || {}, 'code', isEn),
    metricsKeyList: metricsList?.map(item => item?.key_metric || '')
  }), [gameList, languageList, metricsList, isEn, selfGame]);

  /**
   * 处理语言更改事件。
   * 
   * @param value - 选择的语言数组。
   * 
   * 用于更新当前选择的语言状态。
   */
  const onLanguageChange = (value: string[]) => {
    setLanguage(value);
  };

  /**
   * 处理游戏选择变化的回调函数。
   * @param value - 选择的游戏值。
   *  用于更新当前选择的游戏状态。
   */
  const onGameChange = (value: string) => {
    setGame(value);
  };

  // 日期筛选器渲染
  const renderRangePicker = () => (
    <RangePicker
      type={granularity}
      value={timeValue}
      showShortcut={false}
      className="search-border"
      // defualtSelectedShortcut={dateFilterConfig?.defualtSelectedShortcut || {}}
      typeList={[granularity]}
      language={isEn ? 'en' : 'cn'}
      showSelectType={false}
      startOfWeek={dateFilterConfig.startOfWeek}
      onChange={(value: any) => setTimeValue(value)}
      showShortcutTypeTitle={true}
      overlayClassName="dbh-studio-social-range-picker-overlay"
      singleWeek={true}
      disabledDate={{
        "week": (date: Moment) => {
          // 下周一的开始
          const startOfNextWeek = moment().endOf("week").add(1, "day");
          // 禁用下周一及之后的日期
          return date.isBefore(startOfNextWeek, "day");
        },
      }}

      {...(dateFilterConfig?.shortcutOption && { setShortcutOption: () => dateFilterConfig?.shortcutOption })}
    />
  );

  return (
    <ConfigProvider locale={isEn ? 'zh' : 'en'}>
      <div className="dbo-ingame-analytics" style={{ height: '100%' }}>
        <Abnormal
          isLoading={filterLoading}
          isError={error}
          // 733 为保持和外部loading高度一致（页面配置获取的loading）
          style={{ height: '733px' }}
          component={
            <>
              <div className="dbh-studio-social-filter">
                <div className="search-item search-item-date">
                  {renderRangePicker()}
                </div>
                <SingleSelect
                  className={`dbh-studio-social-filter-game game-${bindClassName.current}`}
                  boxClassName='dbh-studio-social-filter-game-box'
                  options={gameOptions}
                  value={game}
                  searchable={false}
                  locale={isEn ? LANGUAGE_EN : LANGUAGE_CN}
                  onChange={onGameChange}
                  name={getName(isEn, gameFilterEnName, gameFilterCnName)}
                  tooltip={getName(isEn, gameFilterEnTooltip, gameFilterCnTooltip)}
                  matchButtonWidth={true}
                  popupContainer={ () => document.querySelector(`.game-${bindClassName.current}`)}
                  escapeWithReference={true}
                />
                <FilterSelect
                  value={language}
                  locale={isEn ? LANGUAGE_EN : LANGUAGE_CN}
                  className={`dbh-studio-social-filter-language language-${bindClassName.current}`}
                  name={getLanguage(isEn, languageFilterEnName, languageFilterCnName)}
                  placeholder='All'
                  tooltip={getName(isEn, languageFilterEnTooltip, languageFilterCnTooltip)}
                  listWidth={265}
                  clickClose={false}
                  closeOnScroll={false}
                  onConfirm={onLanguageChange}
                  isRevertToLastSelection={true}
                  options={languageOptions}
                  popupContainer={ () => document.querySelector(`.language-${bindClassName.current}`)}
                  escapeWithReference={true}
                  {...(maxSelectedNum ? { maxSelect: maxSelectedNum } : {})}
                />
                <div className="dbh-studio-social-filter-right-text">
                  <div className='dbh-studio-social-filter-right-text-center'>
                    <img src={AIX} className="dbh-studio-social-filter-right-text-icon" />
                    <span>{getName(isEn, rightTextEn, rightTextCn)}</span>
                  </div>
                </div>
              </div>
              {children && React.Children.map(children, child =>
                React.cloneElement(child, {
                  filter,
                  gameInfo,
                  metricsList,
                  gameList,
                  languageList,
                  gameMap,
                  languageMap,
                  metricsKeyList,
                  selfGame
                }))}
            </>

          }
        />
      </div>
    </ConfigProvider>
  );
}
